import {
  Box,
  type BoxProps,
  Button,
  Flex,
  Link,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react'
import { cx } from '@chakra-ui/utils'
import NextLink from 'next/link'
import { useRouter } from 'next/router'

import { ChevronDown } from '../../../Assets/icons'
import { useNavigation } from '../../../context/NavigationProvider'
import { pxToRem } from '../../../utils/pxToRem'
import Container from '../../grid/Container/Container'
import { EntryId } from '../../shared-components/DocumentToReactRender/DocumentToReactRender'
import SearchPages from '../../shared-components/SearchPagesDropdown/SearchPagesDropdown'

interface NavItem {
  id: EntryId
  text: string
}

interface LoginLink {
  id: EntryId
  text: string
  url: string
}

export interface TopNavProps extends BoxProps {
  navItems: NavItem[]
  loginText: string
  loginLinks: LoginLink[]
}

export default function TopNav({
  className,
  navItems,
  loginText,
  loginLinks,
  ...boxProps
}: TopNavProps) {
  const { findFullUrlFromId } = useNavigation()

  const router = useRouter()

  const currentPath = router.asPath

  return (
    <Box
      className={cx('top-nav', className)}
      bg="primary.teal.100"
      color="white"
      {...boxProps}
    >
      <Container>
        <Flex w="100%" justify="space-between" flexWrap="nowrap">
          <Flex w="100%" h="40px" align="center" as={List}>
            {navItems.map((item, index) => {
              const path = findFullUrlFromId(item.id) ?? ''
              const isSelected = path && currentPath.startsWith(`/${path}`)

              return (
                <ListItem key={`${item.id}-${index}`} h="100%">
                  <Link
                    as={NextLink}
                    href={`/${path}`}
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    h="100%"
                    sx={{
                      '&:last-child': {
                        marginLeft: 'auto',
                      },
                    }}
                    p="9px 15px"
                    whiteSpace="nowrap"
                    fontWeight={isSelected ? '400' : '300'}
                    lineHeight={pxToRem(18)}
                    fontSize={pxToRem(16)}
                    textAlign="center"
                    position="relative"
                    _hover={{
                      textDecoration: 'none',
                      '> .bar': {
                        opacity: 0.7,
                      },
                    }}
                  >
                    {item.text}

                    <Box
                      className="bar"
                      roundedTop="2.5px"
                      h="4px"
                      bg="white"
                      position="absolute"
                      bottom="0"
                      right="0"
                      left="0"
                      opacity={isSelected ? 1 : 0}
                      transition="0.3s"
                    />
                  </Link>
                </ListItem>
              )
            })}
          </Flex>

          <Flex gap="28px" align="center">
            <SearchPages />

            <Menu variant="loginStyles">
              {({ isOpen }) => (
                <>
                  <MenuButton
                    as={Button}
                    variant="primarySmall"
                    whiteSpace="nowrap"
                  >
                    <Flex gap="10px" alignItems="center">
                      {loginText}

                      <ChevronDown
                        transform={isOpen ? 'rotate(180deg)' : 'rotate(360deg)'}
                        transition="transform 0.3s ease-in-out"
                      />
                    </Flex>
                  </MenuButton>
                  <MenuList>
                    {loginLinks.map((loginLink, index) => {
                      return (
                        <MenuItem
                          key={`${loginLink.id}-${index}`}
                          as="a"
                          href={loginLink.url ?? ''}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {loginLink.text}
                        </MenuItem>
                      )
                    })}
                  </MenuList>
                </>
              )}
            </Menu>
          </Flex>
        </Flex>
      </Container>
    </Box>
  )
}
